import { Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { InlineHint } from "../../../../../components/UIToolkit/molecules/InlineHint";
import { StatStrip, type StatStripItem } from "../../../../../components/UIToolkit/molecules/StatStrip";
import { SystemBanner } from "../../../../../components/UIToolkit/molecules/SystemBanner";
import { NEUTRAL } from "../../../../../theme/tokens";
import type { IntegrationShiftsSummary } from "../../../../../utils/models";
import { configurationSectionSx, formatRatio, sectionSx } from "./consts";
import { EmptySummaryState } from "./EmptySummaryState";
import { MissingRecurringPatternsGrid } from "./MissingRecurringPatternsGrid";
import { SectionHeading } from "./SectionHeading";
import { ShiftMismatchEmployeesGrid } from "./ShiftMismatchEmployeesGrid";
import { ShiftMismatchesGrid } from "./ShiftMismatchesGrid";
import { ShiftsDailyChart } from "./ShiftsDailyChart";

/** Which empty state fits a window with nothing to compare. Copies only exist once a sync has run. */
const windowEmptyKeys = (summary: IntegrationShiftsSummary, hasExclusions: boolean): { title: string; hint: string } => {
	if (!summary.hasEverSynced) return { title: "integration.summary.notRunYet.title", hint: "integration.summary.notRunYet.hint" };
	if (hasExclusions) return { title: "integration.summary.shifts.window.allExcluded.title", hint: "integration.summary.shifts.window.allExcluded.hint" };
	return { title: "integration.summary.shifts.window.empty.title", hint: "integration.summary.shifts.window.empty.hint" };
};

export const ShiftsSummaryContent: FC<{ summary: IntegrationShiftsSummary }> = ({ summary }) => {
	const { t, i18n } = useTranslation();
	const stats: StatStripItem[] = [
		{ label: t("integration.summary.shifts.stat.scheduled"), value: summary.scheduledCount, dataTestId: "hris-shifts-summary-scheduled-count" },
		{ label: t("integration.summary.shifts.stat.actual"), value: summary.actualCount, dataTestId: "hris-shifts-summary-actual-count" },
		{
			label: t("integration.summary.shifts.stat.noPunches"),
			value: formatRatio(summary.noPunchesRatio, i18n.language),
			// The diagnostic figure: scheduled shifts the HRIS sent no punches for.
			emphasis: true,
			dataTestId: "hris-shifts-summary-no-punches",
		},
		{
			label: t("integration.summary.shifts.stat.noSchedules"),
			value: formatRatio(summary.noSchedulesRatio, i18n.language),
			dataTestId: "hris-shifts-summary-no-schedules",
		},
	];
	const hasShifts = summary.scheduledCount > 0 || summary.actualCount > 0;
	const hasExclusions = summary.copiedActualCount > 0 || summary.excludedScheduledCount > 0;
	const emptyKeys = windowEmptyKeys(summary, hasExclusions);
	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2, color: NEUTRAL[900] }} data-testid="hris-shifts-summary-content">
			<Box sx={(theme) => sectionSx(theme)} data-testid="hris-shifts-summary-window-section">
				<SectionHeading title={t("integration.summary.shifts.window.title")} description={t("integration.summary.shifts.window.description")} />
				<InlineHint tone="neutral" dataTestId="hris-shifts-summary-ratios-hint">
					{t("integration.summary.shifts.ratios.hint")}
				</InlineHint>
				<StatStrip items={stats} variant="band" dataTestId="hris-shifts-summary-stats" />
				{hasExclusions && (
					<InlineHint tone="info" dataTestId="hris-shifts-summary-excluded-note">
						{t("integration.summary.shifts.excluded.note", { copied: summary.copiedActualCount, excluded: summary.excludedScheduledCount })}
					</InlineHint>
				)}
				{hasShifts ? (
					<>
						<ShiftsDailyChart daily={summary.daily} />
						<ShiftMismatchEmployeesGrid section={summary.employees} />
						<ShiftMismatchesGrid section={summary.mismatches} />
					</>
				) : (
					<EmptySummaryState title={t(emptyKeys.title)} hint={t(emptyKeys.hint)} testId="hris-shifts-summary-window-empty" />
				)}
			</Box>
			<Box sx={(theme) => configurationSectionSx(theme)} data-testid="hris-shifts-summary-configuration-section">
				<SystemBanner
					tone="warning"
					title={t("integration.summary.configuration.title")}
					description={t("integration.summary.configuration.description")}
					dataTestId="hris-shifts-summary-configuration-banner"
				/>
				<MissingRecurringPatternsGrid />
			</Box>
		</Box>
	);
};
